import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import {
  GroupPermissionControlArgs,
  Permission,
  PermissionGroup,
  ShortPermissionGroup,
  UserPermissionControlArgs,
} from './types';
import { toPermissionGroup } from './utils';

// Define a service using a base URL and expected endpoints
export const permissionsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getPermissions: build.query<CognitoAPIResponse<Permission[]>, { prefix?: string }>({
      query(arg) {
        return {
          url: `permissions/query/${arg?.prefix ?? ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Permissions'],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.value);
      },
    }),
    getPermission: build.query<CognitoAPIResponse<Permission>, { tag: string }>({
      query(arg) {
        return {
          url: `permissions/${arg.tag}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{ type: 'Permissions', id: arg.tag }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => entry.value);
      },
    }),
    updatePermission: build.mutation<any, { tag: string, newAttributes: any }>({
      query(arg) {
        const { tag, newAttributes } = arg;
        return {
          url: `permissions/${tag}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Permissions', id: arg.tag }],
    }),
    sharePermission: build.mutation<any, UserPermissionControlArgs | GroupPermissionControlArgs>({
      query(arg) {
        if (arg?.userId) {
          const { tag, userId } = arg;
          return {
            url: `permissions/${tag}/share`,
            body: { userId },
            method: 'PUT',
          };
        }
        const { tag, groupId, type } = arg;
        return {
          url: `permissions/${tag}/share`,
          body: { group: groupId, type },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Permissions', id: arg.tag },
        { type: 'PermissionGroups', id: arg?.groupId },
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    unsharePermission: build.mutation<any, UserPermissionControlArgs | GroupPermissionControlArgs>({
      query(arg) {
        if (arg?.userId) {
          const { tag, userId } = arg;
          return {
            url: `permissions/${tag}/unshare`,
            body: { userId },
            method: 'PUT',
          };
        }
        const { tag, groupId, type } = arg;
        return {
          url: `permissions/${tag}/unshare`,
          body: { group: groupId, type },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Permissions', id: arg.tag },
        { type: 'PermissionGroups', id: arg?.groupId },
        { type: 'Users', id: arg?.userId },
        { type: 'Students', id: arg?.userId },
        { type: 'Staff', id: arg?.userId },
      ],
    }),
    getGroups: build.query<CognitoAPIResponse<ShortPermissionGroup[]>, {}>({
      query() {
        return {
          url: 'permissions/group/query',
          method: 'GET',
        };
      },
      providesTags: ['PermissionGroups'],
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.groupId);
      },
    }),
    getGroup: build.query<CognitoAPIResponse<PermissionGroup>, { group: string }>({
      query(arg) {
        const { group } = arg;
        return {
          url: `permissions/group/${group}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{ type: 'PermissionGroups', id: arg.group }],
      transformResponse(response: any) {
        return toPermissionGroup(response);
      },
    }),
    addGroupMember: build.mutation<any, { group: string, userId: string }>({
      query(arg) {
        const { group, userId } = arg;
        return {
          url: `permissions/group/${group}/add`,
          body: { userId },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'PermissionGroups', id: arg.group }],
    }),
    removeGroupMember: build.mutation<any, { group: string, userId: string }>({
      query(arg) {
        const { group, userId } = arg;
        return {
          url: `permissions/group/${group}/remove`,
          body: { userId },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'PermissionGroups', id: arg.group }],
    }),
    newGroup: build.mutation<any, { groupId: string, name: string }>({
      query(arg) {
        const { groupId, name } = arg;
        return {
          url: 'permissions/group/new',
          method: 'POST',
          body: { groupId, name },
        };
      },
      invalidatesTags: ['PermissionGroups'],
    }),
    updatePermissionGroup: build.mutation<any, { group: string, attributes: object }>({
      query(arg) {
        const { attributes, group } = arg;
        return {
          url: `permissions/group/${group}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'PermissionGroups', id: arg.group }],
    }),
  }),
});

export const {
  useGetPermissionQuery,
  useLazyGetPermissionQuery,
  useGetPermissionsQuery,
  useNewGroupMutation,
  useUpdatePermissionMutation,
  useSharePermissionMutation,
  useUnsharePermissionMutation,
  useGetGroupsQuery,
  useGetGroupQuery,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useUpdatePermissionGroupMutation,
} = permissionsApi;
